/**
 * MACHINE-UX — Diagnostics facet (§5.2).
 * Presence / freshness / remediation honesty only.
 * Pins, ports, and board stay on Hardware. No pass/fail theatre.
 */
import { useMemo } from "react";
import { html, Alert, Ico, go, cmcPlainOf } from "./ui.js";
import { path, useApi } from "./api.js";
import { presenceDiagnoseView } from "./fleet-live.js";
import { safeBoardPath } from "./board-api.js";
import { explainBoardError } from "./board-seat-adapter.js";
import { confirmLeaveSeat } from "./shell-wayfind.js";
import { CAP_HOME } from "./cmc-entry-registry.js";
import { operatorSay } from "./operator-say.js";

function safePath(op, opts) {
  try {
    return path(op, opts);
  } catch {
    return safeBoardPath(op, opts) || "";
  }
}

function fmtAge(s) {
  if (s == null || !Number.isFinite(s)) return "—";
  if (s < 60) return Math.floor(s) + "s";
  if (s < 3600) return Math.floor(s / 60) + "m";
  return Math.floor(s / 3600) + "h";
}

export function MachineDiagnosticsPanel({ deviceId, toast, fromRoute }) {
  const did = String(deviceId || "").trim();
  const here = fromRoute || ("/machine/" + encodeURIComponent(did) + "/diagnostics");
  const diagUrl = did ? safePath("get__devices_p_presence-diagnose", { pathParams: [did] }) : null;
  const diagLive = useApi(diagUrl);
  const diag = useMemo(() => presenceDiagnoseView(diagLive.data), [diagLive.data]);
  const online = !!(diag && diag.online);
  const ages = (diag && diag.ages) || {};

  const reload = () => {
    diagLive.reload && diagLive.reload();
    toast && toast("Diagnostics re-read", "go");
  };

  if (!did) {
    return html`<p className="note">Pick a machine from the list.</p>`;
  }

  return html`<div className="machine-dx" data-cmc-m35-facet="diagnostics" data-cmc-shell="machine-ux"
      data-cmc-soft-theatre="forbidden" data-device=${did}>
    <${Alert} tone="info" title="Diagnostics"
      say="Presence and freshness from the account register. Pass/fail theatre stays deleted. Pins stay on Hardware."/>

    <div className="btn-row" style=${{ flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
      <button type="button" className="btn btn--ghost btn--sm" onClick=${reload}>
        <${Ico} d="refresh" w=${13}/> Re-read</button>
      <button type="button" className="btn btn--ghost btn--sm"
        onClick=${() => go("/machine/" + encodeURIComponent(did) + "/hardware")}>
        Open Hardware</button>
      <button type="button" className="btn btn--ghost btn--sm"
        onClick=${() => confirmLeaveSeat({
          fromRoute: here,
          targetPath: CAP_HOME.edge_lab.path,
          objectLabel: did,
        })}>
        <${Ico} d="radio" w=${13}/> Lab bench</button>
    </div>

    ${diagLive.loading && !diag
      ? html`<p className="note">Reading presence…</p>`
      : null}
    ${diagLive.error
      ? html`<${Alert} tone="warn" title="Presence unavailable"
          say=${cmcPlainOf(explainBoardError(diagLive.error))}/>`
      : null}
    ${!diagLive.loading && !diagLive.error && !diag
      ? html`<${Alert} tone="warn" title="No diagnose frame"
          say="Empty stays empty — this seat does not invent a health report."/>`
      : null}

    ${diag
      ? html`<div className="machine-hw__kpis">
          <div className="machine-hw__kpi" data-on=${online ? "1" : "0"}>
            <span>Presence</span>
            <b>${online ? "Online" : "Quiet"}</b>
            <em>${ages.heartbeat != null ? ("hb " + fmtAge(ages.heartbeat)) : "age —"}</em>
          </div>
          <div className="machine-hw__kpi" data-on=${ages.signal != null ? "1" : "0"}>
            <span>Signal age</span>
            <b>${fmtAge(ages.signal)}</b>
            <em>${diag.reason ? operatorSay(diag.reason, "Signal path") : "path —"}</em>
          </div>
          <div className="machine-hw__kpi" data-on=${diag.problem ? "1" : "0"}>
            <span>Suspect</span>
            <b>${diag.problem ? "Flagged" : "Clear"}</b>
            <em>${diag.problem ? operatorSay(diag.suspect, "Flagged") : (diag.suspect === "B8_http_board_live" ? "HTTP board live" : "no flag in frame")}</em>
          </div>
          <div className="machine-hw__kpi" data-on=${ages.updated != null ? "1" : "0"}>
            <span>Updated</span>
            <b>${fmtAge(ages.updated)}</b>
            <em>last frame</em>
          </div>
        </div>`
      : null}

    <p className="note" style=${{ marginTop: 12 }}>
      Reboot, siren, and self-test stay on authorized desks after confirm — not invented here.
    </p>
  </div>`;
}

export default MachineDiagnosticsPanel;
